import React, {
  useState, useContext, useEffect, useCallback,
} from 'react';
import propTypes from 'prop-types';
import {
  FormControl,
} from '@material-ui/core';

import Header from 'components/CardShell/Header';
import SelectWithValidation from 'components/SelectWithValidation';
import DualButtons from 'components/DualButtons';
import InputWithValidation from 'components/InputWithValidation';
import { validateForm } from 'helpers/validateForm';
import Checkbox from 'components/Checkbox';
import styles from './styles.scss';
import ShipmentFilterContext from '../ShipmentFiltersContext';

const RestrictTrigger = (props) => {
  const {
    title, pageTabs, cancel, gotoPage, updateState, formData,
  } = props;

  const { metadataGroupings } = useContext(ShipmentFilterContext);

  const [singleTriggerPerShipmentGroup, setSingleTriggerPerShipmentGroup] = useState(formData.single_trigger_per_shipment_group);
  const [singleTriggerGroupingMetakey, setSingleTriggerGroupingMetakey] = useState(formData.single_trigger_grouping_metakey);
  const [singleTriggerTimespan, setSingleTriggerTimeSpan] = useState(formData.single_trigger_timespan_in_minutes);

  const [errors, setErrors] = useState(null);
  const maxTriggerResetTime = 130000;

  const validationRules = {
    singleTriggerGroupingMetakey: [
      {
        name: 'required',
        message: () => 'This field is mandatory if the checkbox is ticked',
        isValid: () => !singleTriggerPerShipmentGroup || singleTriggerGroupingMetakey,
      },
      {
        name: 'changed group',
        message: () => 'This field is mandatory if the checkbox is ticked',
        isValid: () => !singleTriggerPerShipmentGroup
          || metadataGroupings.filter(g => g.metadataKey === singleTriggerGroupingMetakey).length > 0,
      },
    ],
    singleTriggerTimespan: [
      {
        name: 'required',
        message: () => 'This field is mandatory if the checkbox is ticked',
        isValid: () => !singleTriggerPerShipmentGroup || singleTriggerTimespan,
      },
      {
        name: 'minValue',
        message: () => 'Min trigger reset time is 1 minute',
        isValid: () => !singleTriggerPerShipmentGroup || singleTriggerTimespan >= 1,
      },
      {
        name: 'maxValue',
        message: () => `Max trigger reset time is ${maxTriggerResetTime} minutes (3 months)`,
        isValid: () => singleTriggerTimespan <= maxTriggerResetTime,
      },
    ],
  };

  const handleNextButtonClick = () => {
    const formValidation = validateForm(validationRules, { singleTriggerPerShipmentGroup, singleTriggerGroupingMetakey, singleTriggerTimespan });

    if (!formValidation.hasErrors) {
      setErrors(formValidation.hasErrors);
      updateState({
        single_trigger_per_shipment_group: singleTriggerPerShipmentGroup,
        single_trigger_grouping_metakey: singleTriggerPerShipmentGroup ? singleTriggerGroupingMetakey : null,
        single_trigger_timespan_in_minutes: singleTriggerPerShipmentGroup ? singleTriggerTimespan : 0,
      });
      gotoPage(7);
    } else {
      setErrors(formValidation);
    }
  };

  const handleTimespanChange = (e) => {
    setSingleTriggerTimeSpan(e.target.value.replace(/[^0-9]/g, ''));
  };

  const saveState = useCallback(() => {
    updateState({
      single_trigger_per_shipment_group: singleTriggerPerShipmentGroup,
      single_trigger_grouping_metakey: singleTriggerPerShipmentGroup ? singleTriggerGroupingMetakey : null,
      single_trigger_timespan_in_minutes: singleTriggerPerShipmentGroup ? singleTriggerTimespan : 0,
    });
  }, [singleTriggerTimespan, singleTriggerGroupingMetakey, singleTriggerPerShipmentGroup, updateState]);

  useEffect(() => {
    saveState();
  }, [saveState]);

  return (
    <div className={styles.cardSection}>
      <div className={styles.cardSectionNav}>
        {pageTabs()}
      </div>
      <div className={styles.cardSectionInner}>
        <Header>{title}</Header>
        <p className={styles.innerContentTitle}>
          You can restrict triggering of this shipment filter to once per shipment grouping. Shipment Groupings must be set up first.
          This step is optional and should only be used if you know it is needed.
          Restriction is time based and can last from 1 minute to 3 months (130000 minutes).
        </p>
        <Checkbox
          label="Trigger once per grouping?"
          checked={singleTriggerPerShipmentGroup}
          onChange={() => { setSingleTriggerPerShipmentGroup(!singleTriggerPerShipmentGroup); }}
        />
        {singleTriggerPerShipmentGroup && (
        <FormControl fullWidth>
          <div className={styles.inputGroup}>
            <SelectWithValidation
              label="Grouping Metakey"
              name="singleTriggerGroupingMetakey"
              value={singleTriggerGroupingMetakey}
              defaultValue={null}
              onChange={(e) => { setSingleTriggerGroupingMetakey(e.value); }}
              options={[...metadataGroupings.map(x => ({
                value: x.metadataKey,
                label: x.metadataKey,
              }))]}
              errors={errors}
            />
          </div>
          <div className={styles.inputGroup}>
            <InputWithValidation
              label="Trigger reset in minutes"
              placeholder="Trigger reset in minutes"
              onChange={handleTimespanChange}
              value={singleTriggerTimespan}
              type="text"
              name="singleTriggerTimespan"
              errors={errors}
            />
          </div>
        </FormControl>
        )}
      </div>
      <div className={styles.cardSectionButtons}>
        <DualButtons
          leftFunction={cancel}
          leftText="Cancel"
          rightFunction={handleNextButtonClick}
          rightText="Next"
          theme="rightPink"
        />
      </div>
    </div>
  );
};

RestrictTrigger.propTypes = {
  pageTabs: propTypes.func.isRequired,
  cancel: propTypes.func.isRequired,
  gotoPage: propTypes.func.isRequired,
  updateState: propTypes.func.isRequired,
  formData: propTypes.shape({
    single_trigger_per_shipment_group: propTypes.bool,
    single_trigger_grouping_metakey: propTypes.string,
    single_trigger_timespan_in_minutes: propTypes.number,
  }).isRequired,
};

export default RestrictTrigger;
